import { Link } from 'react-router-dom';

/**
 * Two-tab switch for a single conversation: Dashboard (charts grid)
 * vs Conversation (full chat thread).
 *
 * URL convention:
 *   - /c/<id>/dashboard       → dashboard view
 *   - /c/<id>?view=chat       → chat view (the `?view=chat` flag opts out
 *                               of the smart default-redirect-to-dashboard
 *                               that ChatPage performs when a conversation
 *                               has charts)
 *   - /c/<id>                 → ChatPage decides: dashboard if charts exist,
 *                               otherwise chat
 *
 * The "Conversation" tab links with `?view=chat` so clicking it from the
 * dashboard explicitly opts the user out of the auto-redirect. The
 * "Dashboard" tab links to the explicit /dashboard URL.
 */
export function ConversationTabs({
  conversationId,
  current,
  chartCount,
}: {
  conversationId: string;
  current: 'chat' | 'dashboard';
  chartCount?: number;
}) {
  return (
    <div
      role="tablist"
      aria-label="Conversation view"
      className="inline-flex rounded-lg border border-slate-200 bg-slate-100/70 p-1"
    >
      <Link
        to={`/c/${conversationId}/dashboard`}
        role="tab"
        aria-selected={current === 'dashboard'}
        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-all ${
          current === 'dashboard'
            ? 'bg-white text-slate-900 shadow-sm font-medium'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <DashboardIcon />
        <span>Dashboard</span>
        {typeof chartCount === 'number' && chartCount > 0 && (
          <span
            className={`ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
              current === 'dashboard'
                ? 'bg-wb-100 text-wb-800'
                : 'bg-slate-200 text-slate-600'
            }`}
          >
            {chartCount}
          </span>
        )}
      </Link>
      <Link
        to={`/c/${conversationId}?view=chat`}
        role="tab"
        aria-selected={current === 'chat'}
        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-all ${
          current === 'chat'
            ? 'bg-white text-slate-900 shadow-sm font-medium'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <ChatIcon />
        <span>Conversation</span>
      </Link>
    </div>
  );
}

function DashboardIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
